(() => {
  // Section scripts can be evaluated again by the theme editor.
  if (customElements.get('facet-filters-form')) return;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.handleInput = (event) => {
      if (event.target.closest('facet-filters-form') !== this) return;
      clearTimeout(this.submitTimeout);
      FacetFiltersForm.cancelPendingRequest();
      this.submitTimeout = setTimeout(() => {
        if (this.isConnected && !this.shouldPreventSubmit(event)) this.onSubmitHandler(event);
      }, 800);
    };
    this.handleSubmit = (event) => {
      if (event.target.closest('facet-filters-form') !== this) return;
      event.preventDefault();
      clearTimeout(this.submitTimeout);
      if (!this.shouldPreventSubmit(event)) this.onSubmitHandler(event);
    };
  }

  connectedCallback() {
    this.listenersController?.abort();
    this.listenersController = new AbortController();
    const options = { signal: this.listenersController.signal };
    // An outer utility element may contain a nested sorting element; each owns its events.
    this.addEventListener('input', this.handleInput, options);
    this.addEventListener('submit', this.handleSubmit, options);
    this.querySelector('#FacetsWrapperDesktop')?.addEventListener('keyup', onKeyUpEscape, options);
    FacetFiltersForm.initializeShowMore();
  }

  disconnectedCallback() {
    clearTimeout(this.submitTimeout);
    this.listenersController?.abort();
  }

  static setListeners() {
    window.addEventListener('popstate', () => {
      // Other theme components can replace history.state. The URL remains authoritative.
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev && !FacetFiltersForm.controller) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains?.(document.getElementById('ProductGridContainer'))) {
        FacetFiltersForm.cancelPendingRequest();
      }
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('facet-remove a').forEach(element => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoading(loading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.querySelector('.collection-grid-container')?.classList.toggle('loading', loading);
    if (loading) grid?.setAttribute('aria-busy', 'true');
    else grid?.removeAttribute('aria-busy');
    ['ProductCount', 'ProductCountDesktop'].forEach(id => {
      document.getElementById(id)?.classList.toggle('loading', loading);
    });
    if (!loading) FacetFiltersForm.toggleActiveFacets(false);
  }

  static cancelPendingRequest() {
    FacetFiltersForm.requestId += 1;
    FacetFiltersForm.controller?.abort();
    FacetFiltersForm.controller = null;
    FacetFiltersForm.setLoading(false);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.cancelPendingRequest();
    const section = FacetFiltersForm.getSections()[0];
    const params = new URLSearchParams(searchParams);
    params.delete('section_id');
    const requestedParams = params.toString();
    const destination = `${window.location.pathname}${requestedParams ? '?' + requestedParams : ''}`;
    const grid = document.getElementById('ProductGridContainer');
    if (!section || !grid) {
      window.location.assign(destination);
      return;
    }
    const request = {
      id: FacetFiltersForm.requestId,
      grid,
      params: requestedParams,
      destination,
      event,
      updateURLHash,
    };
    FacetFiltersForm.controller = new AbortController();
    request.signal = FacetFiltersForm.controller.signal;
    FacetFiltersForm.setLoading(true);
    params.set('section_id', section.section);
    const url = `${window.location.pathname}?${params.toString()}`;
    const cached = FacetFiltersForm.filterData.find(entry => entry.url === url);
    if (cached) FacetFiltersForm.renderSection(cached.html, request);
    else FacetFiltersForm.renderSectionFromFetch(url, request);
  }

  static async renderSectionFromFetch(url, request) {
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Filter request failed');
      const html = await response.text();
      if (!FacetFiltersForm.isCurrentRequest(request)) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (!parsed.getElementById('ProductGridContainer')) throw new Error('Missing product grid');
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.slice(-19), { html, url }];
      FacetFiltersForm.renderSection(parsed, request);
    } catch (error) {
      if (error.name === 'AbortError' || !FacetFiltersForm.isCurrentRequest(request)) return;
      FacetFiltersForm.controller = null;
      FacetFiltersForm.setLoading(false);
      // Fall back to Shopify's native page, preserving the requested query/filter/sort exactly.
      window.location.assign(request.destination);
    }
  }

  static isCurrentRequest(request) {
    return request.id === FacetFiltersForm.requestId && !request.signal.aborted &&
      request.grid === document.getElementById('ProductGridContainer');
  }

  static renderSection(html, request) {
    if (!FacetFiltersForm.isCurrentRequest(request)) return;
    const parsed = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const sourceGrid = parsed.getElementById('ProductGridContainer');
    if (!sourceGrid) {
      FacetFiltersForm.controller = null;
      FacetFiltersForm.setLoading(false);
      window.location.assign(request.destination);
      return;
    }
    FacetFiltersForm.renderFilters(parsed);
    FacetFiltersForm.renderProductGridContainer(sourceGrid);
    FacetFiltersForm.renderProductCount(parsed);
    FacetFiltersForm.renderSort(parsed);
    FacetFiltersForm.searchParamsPrev = request.params;
    FacetFiltersForm.controller = null;
    FacetFiltersForm.setLoading(false);
    FacetFiltersForm.initializeShowMore();
    if (request.updateURLHash) FacetFiltersForm.updateURLHash(request.params);
    document.querySelector('quick-cart-drawer')?.initTriggers();
  }

  static renderProductGridContainer(source) {
    const target = document.getElementById('ProductGridContainer');
    target.innerHTML = source.innerHTML;
    target.querySelectorAll('.scroll-trigger').forEach(element => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(parsed) {
    const source = parsed.getElementById('ProductCount');
    const target = document.getElementById('ProductCount');
    const sourceStatus = source?.closest('.collection-facets__status');
    const targetStatus = target?.closest('.collection-facets__status');
    if (sourceStatus && targetStatus) targetStatus.innerHTML = sourceStatus.innerHTML;
    else if (source && target) target.innerHTML = source.innerHTML;
    const desktop = document.getElementById('ProductCountDesktop');
    const desktopSource = parsed.getElementById('ProductCountDesktop') || source;
    if (desktop && desktopSource) desktop.innerHTML = desktopSource.innerHTML;
  }

  static renderSort(parsed) {
    const source = parsed.getElementById('SortBy');
    const target = document.getElementById('SortBy');
    if (!source || !target) return;
    const selected = Array.from(source.options).find(option => option.selected);
    if (!selected) return;
    target.value = selected.value;
    Array.from(target.options).forEach(option => {
      option.selected = option.value === selected.value;
      if (option.selected) option.setAttribute('selected', 'selected');
      else option.removeAttribute('selected');
    });
    // Synchronize the existing widget without replacing it or emitting a second input event.
    const widget = target.closest('custom-select');
    if (!widget) return;
    widget.dataset.value = selected.value;
    widget.querySelectorAll('.js-btn-option').forEach(button => {
      const active = button.dataset.value === selected.value;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-selected', String(active));
    });
    const toggle = widget.querySelector('.js-btn-dropdown');
    if (toggle) toggle.replaceChildren(document.createTextNode(selected.textContent + ' '), ...Array.from(toggle.children));
  }

  static renderFilters(parsed) {
    // Sidebar controls are inside the grid and are replaced with it. Reconcile the drawer only.
    const outsideGrid = element => !element.closest('#ProductGridContainer');
    const selector = 'facet-filters-form form .js-filter[id]';
    const sources = Array.from(parsed.querySelectorAll(selector)).filter(outsideGrid);
    const targets = Array.from(document.querySelectorAll(selector)).filter(outsideGrid);
    const sourceIds = new Set(sources.map(element => element.id));
    targets.forEach(target => {
      if (!sourceIds.has(target.id)) (target.closest('details.facets__accordion-section') || target).remove();
    });
    sources.forEach((source, index) => {
      const target = targets.find(element => element.id === source.id && element.isConnected);
      if (target) {
        const focused = document.activeElement;
        const focusInside = target.contains(focused);
        const focusId = focused?.id;
        const focusName = focused?.name;
        const focusValue = focused?.value;
        const focusType = focused?.type;
        target.innerHTML = source.innerHTML;
        if (focusInside) {
          const replacement = Array.from(target.querySelectorAll('input, select, button')).find(element =>
            focusId ? element.id === focusId : element.name === focusName && element.type === focusType &&
              (element.type !== 'checkbox' || element.value === focusValue));
          replacement?.focus({ preventScroll: true });
        }
      } else {
        const details = source.closest('details.facets__accordion-section');
        const sourceForm = source.closest('form');
        const accordion = sourceForm && document.getElementById(sourceForm.id)?.querySelector('.facets__accordion');
        if (!details || !accordion) return;
        const nextSource = sources.slice(index + 1).find(element =>
          Array.from(accordion.querySelectorAll('.js-filter[id]')).some(current => current.id === element.id));
        const nextTarget = nextSource && Array.from(accordion.querySelectorAll('.js-filter[id]')).find(element => element.id === nextSource.id);
        if (nextTarget) nextTarget.closest('details.facets__accordion-section').before(details.cloneNode(true));
        else accordion.append(details.cloneNode(true));
      }
    });
    FacetFiltersForm.renderActiveFacets(parsed);
  }

  static renderActiveFacets(parsed) {
    const target = document.querySelector('.active-facets');
    const source = parsed.querySelector('.active-facets');
    if (target) target.innerHTML = source?.innerHTML || '';
  }

  static updateURLHash(searchParams) {
    const destination = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    if (window.location.search.slice(1) === searchParams) return;
    history.pushState({ ...history.state, searchParams }, '', destination);
  }

  static getSections() {
    const grid = document.getElementById('ProductGridContainer');
    const section = document.getElementById('product-grid')?.dataset.id || grid?.dataset.sectionId ||
      document.querySelector('facet-filters-form[data-section-id]')?.dataset.sectionId;
    if (grid && section) grid.dataset.sectionId = section;
    return section ? [{ section }] : [];
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    params.delete('page');
    params.delete('section_id');
    const sort = document.getElementById('SortBy');
    if (sort?.value) params.set('sort_by', sort.value);
    const triggerForm = event.target?.closest('form');
    const forms = Array.from(document.querySelectorAll('facet-filters-form form'));
    const filterForms = forms.filter(form => form.querySelector('[name^="filter."]'));
    // Each drawer/sidebar form contains a complete filter set. Use the changed one,
    // then synchronize the duplicate controls instead of submitting duplicate values.
    const sourceForm = filterForms.includes(triggerForm) ? triggerForm :
      filterForms.find(form => !form.closest('.collection__facets--sidebar')) || filterForms[0];
    if (sourceForm) {
      Array.from(params.keys()).filter(key => key.startsWith('filter.')).forEach(key => params.delete(key));
      const sourceData = new FormData(sourceForm);
      const seen = new Set();
      for (const [name, value] of sourceData) {
        if (!name || value === '' || name === 'sort_by') continue;
        const input = Array.from(sourceForm.elements).find(element => element.name === name);
        if (name === 'filter.v.price.gte' && input?.min !== '' && Number(value) === Number(input?.min || 0)) continue;
        if (name === 'filter.v.price.lte' && input?.max && Number(value) === Number(input.max)) continue;
        if (name.startsWith('filter.')) {
          const key = JSON.stringify([name, value]);
          if (!seen.has(key)) params.append(name, value);
          seen.add(key);
        } else if (!params.has(name)) params.set(name, value);
      }
      filterForms.filter(form => form !== sourceForm).forEach(form => {
        Array.from(form.elements).forEach(input => {
          if (!input.name?.startsWith('filter.')) return;
          if (input.type === 'checkbox') input.checked = sourceData.getAll(input.name).includes(input.value);
          else input.value = sourceData.get(input.name) || '';
        });
      });
    }
    FacetFiltersForm.preserveSearchOptions(params, currentUrl.searchParams);
    this.onSubmitForm(params.toString(), event);
  }

  static preserveSearchOptions(params, current) {
    for (const [key, value] of current) {
      if (key === 'q' || key === 'type' || key === 'sort_by' || key.startsWith('options[')) {
        params.set(key, params.has(key) ? params.get(key) : value);
      }
    }
    if (/\/search\/?$/.test(window.location.pathname)) {
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      params.set('options[unavailable_products]', 'last');
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const target = new URL(event.currentTarget.href, window.location.href);
    FacetFiltersForm.preserveSearchOptions(target.searchParams, new URLSearchParams(window.location.search));
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');
    FacetFiltersForm.renderPage(target.searchParams.toString(), event);
  }

  shouldPreventSubmit(event) {
    const form = event.target?.closest('form');
    const minInput = form?.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = form?.querySelector('input[name="filter.v.price.lte"]');
    if (minInput && maxInput) {
      const min = parseFloat(minInput.value);
      const max = parseFloat(maxInput.value);
      if (!isNaN(min) && !isNaN(max) && min >= max) return true;
    }
    return false;
  }

  static initializeShowMore() {
    document.querySelectorAll('.js-filter[data-show-more-count]').forEach(group => {
      const listItems = group.querySelectorAll('.facets__item');
      const button = group.querySelector('.show-more');
      if (!button) return;
      const limit = parseInt(group.dataset.showMoreCount, 10) || 5;
      const update = () => {
        const expanded = FacetFiltersForm.expandedFilters.has(group.id);
        listItems.forEach((item, index) => item.classList.toggle('is-collapsed', !expanded && index >= limit));
        button.textContent = expanded ? window.actionStrings.showLess : window.actionStrings.showMore;
        button.style.display = listItems.length <= limit ? 'none' : '';
      };
      // Assignment replaces the previous handler when a retained group is refreshed.
      button.onclick = event => {
        event.preventDefault();
        if (FacetFiltersForm.expandedFilters.has(group.id)) FacetFiltersForm.expandedFilters.delete(group.id);
        else FacetFiltersForm.expandedFilters.add(group.id);
        update();
      };
      update();
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.expandedFilters = new Set();
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.initializeShowMore();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach(element => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape'];
    if (!allowedKeys.includes(event.key) && !/[0-9.,]/.test(event.key)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    } else {
      minInput.setAttribute('data-max', maxInput.getAttribute('max'));
    }

    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    } else {
      maxInput.setAttribute('data-min', minInput.getAttribute('min'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min')) || 0; // Default to input's min attribute
    const max = Number(input.getAttribute('max')); // Default to input's max attribute

    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }

  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = { signal: this.#abortController.signal };

    const priceRangeMin = this.querySelector('input[type="range"]:first-child');
    const priceRangeMax = this.querySelector('input[type="range"]:last-child');

    const priceInputMin = this.querySelector('input[name="filter.v.price.gte"]');
    const priceInputMax = this.querySelector('input[name="filter.v.price.lte"]');

    const minPriceGap = priceRangeMin ? parseInt(priceRangeMin.step) || 1 : 1;
    const rangeGroup = this.querySelector('.range-group');

    if (rangeGroup) {
      rangeGroup.addEventListener('click', (event) => {
        if (event.target.tagName === 'INPUT') return;

        const rect = rangeGroup.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
        const percentage = isRtl
          ? 1 - Math.max(0, Math.min(1, x / rect.width))
          : Math.max(0, Math.min(1, x / rect.width));

        if (!priceRangeMin || !priceRangeMax) return;

        const min = parseFloat(priceRangeMin.min);
        const max = parseFloat(priceRangeMin.max);
        const range = max - min;

        const clickedValue = Math.round(min + (range * percentage));

        const currentMin = parseFloat(priceRangeMin.value);
        const currentMax = parseFloat(priceRangeMax.value);

        if (Math.abs(clickedValue - currentMin) < Math.abs(clickedValue - currentMax)) {
          priceRangeMin.value = clickedValue;
          priceRangeMin.dispatchEvent(new Event('input', { bubbles: true }));
          priceRangeMin.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          priceRangeMax.value = clickedValue;
          priceRangeMax.dispatchEvent(new Event('input', { bubbles: true }));
          priceRangeMax.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    const updateRangePosition = (rangeInput, cssProperty) => {
      const percentage = (parseFloat(rangeInput.value) / parseFloat(rangeInput.max)) * 100;
      rangeInput.parentElement.style.setProperty(cssProperty, `${percentage}%`);
    };

    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const validateInput = (input, otherInput, isMin) => {
      const value = parseInt(input.value);
      const absoluteMax = parseInt(input.max);
      const otherValue = parseInt(otherInput.value);

      if (isNaN(value)) return;

      if (isMin) {
        if (value > absoluteMax) {
          input.value = absoluteMax;
        } else if (value >= otherValue) {
          input.value = otherValue - 1;
        }
      } else {
        if (value > absoluteMax) {
          input.value = absoluteMax;
        }
        debounce(() => {
          if (value <= otherValue) {
            input.value = otherValue + 1;
          }
        }, 800);
      }
    };

    priceInputMin.addEventListener('focus', () => priceInputMin.select(), signal);
    priceInputMax.addEventListener('focus', () => priceInputMax.select(), signal);

    priceInputMin.addEventListener('input', () => validateInput(priceInputMin, priceInputMax, true), signal);
    priceInputMax.addEventListener('input', () => validateInput(priceInputMax, priceInputMin, false), signal);

    priceInputMin.addEventListener(
      'change',
      event => {
        event.preventDefault();
        const value = parseInt(event.target.value);
        const maxValue = parseInt(priceInputMax.value || event.target.max);
        const absoluteMin = parseInt(event.target.min);

        event.target.value = clamp(value, absoluteMin, maxValue - minPriceGap);

        if (priceRangeMin) {
          priceRangeMin.value = event.target.value;
          updateRangePosition(priceRangeMin, '--range-min');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceInputMax.addEventListener(
      'change',
      event => {
        event.preventDefault();
        const value = parseInt(event.target.value);
        const minValue = parseInt(priceInputMin.value || event.target.min);
        const absoluteMax = parseInt(event.target.max);

        event.target.value = clamp(value, minValue + minPriceGap, absoluteMax);

        if (priceRangeMax) {
          priceRangeMax.value = event.target.value;
          updateRangePosition(priceRangeMax, '--range-max');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'change',
      event => {
        event.stopPropagation();
        priceInputMin.value = event.target.value;
        priceInputMin.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'change',
      event => {
        event.stopPropagation();
        priceInputMax.value = event.target.value;
        priceInputMax.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'input',
      event => {
        const value = parseInt(event.target.value);
        const maxValue = parseInt(priceInputMax.value);

        event.target.value = Math.min(value, maxValue - minPriceGap);

        updateRangePosition(event.target, '--range-min');
        priceInputMin.value = event.target.value;
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'input',
      event => {
        const value = parseInt(event.target.value);
        const minValue = parseInt(priceInputMin.value);

        event.target.value = Math.max(value, minValue + minPriceGap);

        updateRangePosition(event.target, '--range-max');
        priceInputMax.value = event.target.value;
      },
      signal
    );
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
    }
  }
}

if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', event => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
    else window.location.assign(event.currentTarget.href);
  }
}

if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);

})();
